import { isApiError } from '@botpress/client'
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  showErrorToast,
  showConfirmationPrompt,
  Alert,
  AlertTitle,
  AlertDescription,
} from '@bpinternal/ui-kit-next'
import { HttpStatusCode } from 'axios'
import type { Permission } from '~/features/users/types'
import { wasResolved } from '~/shared/helpers'
import { useDeleteWorkspace, useWorkspaces } from '~/hooks'
import { useQuery } from '~/services'
import type { WorkspaceObject } from '../../workspaces/types'
import { AlertTriangle } from 'lucide-react'

type DangerZoneProps = {
  workspace: WorkspaceObject
  isAuthorized: (permission: Permission) => boolean | undefined
  navigateToHome: () => Promise<void>
}

export function DangerZone({ workspace, isAuthorized, navigateToHome }: DangerZoneProps) {
  const { data: workspaces } = useWorkspaces()

  const { data: bots } = useQuery('workspaces_/$workspaceId_/bots_', { workspaceId: workspace.id })
  const { mutateAsync: deleteWorkspace } = useDeleteWorkspace()

  if (!isAuthorized('workspace.delete')) {
    return null
  }

  function getDeletionRestrictionMessage(): string | undefined {
    if (workspaces && workspaces.length <= 1) {
      return "This workspace cannot be deleted because it's the only workspace currently in your account."
    }
    if (bots?.length && bots.length > 0) {
      return `You must delete all bots before you can delete this workspace. Currently, you have ${bots.length} bots remaining.`
    }
    return undefined
  }

  const cannotDeleteErrorMessage = getDeletionRestrictionMessage()
  const cannotDelete = cannotDeleteErrorMessage !== undefined

  function onClick() {
    if (cannotDelete) {
      showErrorToast(cannotDeleteErrorMessage)
      return
    }
    void showConfirmationPrompt(
      <>
        <p>
          This will permanently delete the workspace &quot;{workspace.name}&quot;. If there's any outstanding balance on
          the workspace, the payment method configured for the workspace will be charged now
          <b> before the workspace can be deleted</b>.
        </p>
        <p>Are you sure you want to proceed?</p>
      </>,
      { title: 'Delete Workspace', variant: 'danger', confirmLabel: 'Yes, delete', size: 'max-w-2xl' }
    ).then(() => void onDelete())
  }

  const onDelete = async () => {
    return wasResolved(
      deleteWorkspace(
        { id: workspace.id },
        {
          onSuccess: () => {
            void navigateToHome()
          },
          onError: (error) => {
            if (isApiError(error)) {
              if (error.code === (HttpStatusCode.PaymentRequired as number)) {
                showErrorToast(
                  'We were unable to process the payment for the outstanding balance on the workspace. ' +
                    'Please check the payment method on file and try to delete the workspace again. ' +
                    'If it looks fine, please contact us.'
                )
              } else {
                showErrorToast(`Sorry, an error occurred while trying to delete the workspace: ${error.message}`)
              }
            }
          },
        }
      )
    )
  }

  return (
    <Card className="w-full">
      <CardContent className="p-6">
        <h3 className="pb-2 text-lg text-red-600">Delete Workspace</h3>
        <p className="text-neutral-900">
          Deleting a workspace is an irreversible process and will permanently erase all data associated with this
          workspace, including invoices and all related information.
        </p>
        {cannotDeleteErrorMessage && (
          <Alert className="mt-4">
            <AlertTriangle className="h-4 w-4" color="orange" />
            <AlertTitle>Action Required</AlertTitle>
            <AlertDescription>{cannotDeleteErrorMessage}</AlertDescription>
          </Alert>
        )}
      </CardContent>
      <CardFooter className="flex justify-end p-6 pt-0">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="destructive" disabled={cannotDelete} onClick={onClick}>
                Delete Workspace
              </Button>
            </TooltipTrigger>
            {cannotDelete && (
              <TooltipContent>
                <p>{cannotDeleteErrorMessage}</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </CardFooter>
    </Card>
  )
}
